import { formatAddress, type NoteStatus } from "@ccnote/shared";
import { useEffect, useMemo, useState } from "react";
import type { Note, SessionUser } from "../api";
import { Composer } from "../components/Composer";
import { CopyButton } from "../components/CopyButton";
import { Logo } from "../components/Logo";
import { NoteDetail } from "../components/NoteDetail";
import { useCategories, useDeleteCategory, useEmptyTrash, useNotes, usePendingCount, useStorage } from "../queries";
import { ConfirmButton } from "../components/ConfirmButton";
import { StorageMini } from "../components/StorageMeter";
import { sourceLabel } from "../format";
import { markdownToPlain } from "../markdown";
import { Link, navigate, setParams, useLocation } from "../router";

const VIEWS: Record<string, { status: NoteStatus; label: string }> = {
  all: { status: "active", label: "All notes" },
  pending: { status: "pending", label: "Pending" },
  archived: { status: "archived", label: "Archive" },
  trashed: { status: "trashed", label: "Trash" },
};

export function NotesPage({ user }: { user: SessionUser }) {
  const { params } = useLocation();
  const view = VIEWS[params.get("view") ?? "all"] ? (params.get("view") ?? "all") : "all";
  const category = view === "all" ? (params.get("c") ?? undefined) : undefined;
  const q = params.get("q") ?? "";
  const openId = params.get("note");
  const composing = params.get("new") === "1";
  const [menuOpen, setMenuOpen] = useState(false);

  const status = VIEWS[view]!.status;
  const title = category ? `#${category}` : VIEWS[view]!.label;

  return (
    <div className={`shell ${openId || composing ? "has-detail" : ""}`}>
      <Sidebar user={user} view={view} category={category} open={menuOpen} onClose={() => setMenuOpen(false)} />

      <section className="list-pane" aria-label={title}>
        <header className="list-header">
          <button className="icon-btn menu-btn" aria-label="Menu" onClick={() => setMenuOpen(true)}>
            <MenuIcon />
          </button>
          <h1>{title}</h1>
          <button className="btn btn-primary btn-small" onClick={() => setParams({ new: "1", note: null })}>
            New note
          </button>
        </header>
        <SearchBox initial={q} />
        {view === "trashed" && <TrashBar />}
        {view === "pending" && (
          <p className="hint">Mail from senders you haven't approved waits here. Approve it to move it into your notes.</p>
        )}
        <NoteList filter={{ status, category, q: q || undefined }} openId={openId} user={user} />
      </section>

      <section className="detail-pane">
        {composing ? (
          <Composer defaultCategory={category} onDone={(id) => setParams({ new: null, note: id ?? null })} />
        ) : openId ? (
          <NoteDetail id={openId} onClose={() => setParams({ note: null })} />
        ) : (
          <EmptyDetail user={user} />
        )}
      </section>
    </div>
  );
}

function Sidebar({
  user,
  view,
  category,
  open,
  onClose,
}: {
  user: SessionUser;
  view: string;
  category?: string;
  open: boolean;
  onClose: () => void;
}) {
  const storage = useStorage();
  const categories = useCategories();
  const deleteCategory = useDeleteCategory();
  const pending = usePendingCount();

  return (
    <>
      <div className={`scrim ${open ? "show" : ""}`} onClick={onClose} />
      <nav className={`sidebar ${open ? "open" : ""}`} aria-label="Folders">
        <div className="sidebar-top">
          <Logo />
        </div>
        <div className="address-card">
          <span className="muted small">Your address</span>
          <strong className="address">{user.address}</strong>
          <CopyButton text={user.address} />
        </div>

        <ul className="nav">
          <NavItem to="/" active={view === "all" && !category} onClick={onClose}>
            All notes
          </NavItem>
          <NavItem to="/?view=pending" active={view === "pending"} onClick={onClose} count={pending.data || undefined} highlight>
            Pending
          </NavItem>
        </ul>

        <h2 className="nav-heading">Categories</h2>
        <ul className="nav">
          {categories.data?.map((c) => (
            <NavItem
              key={c.slug}
              to={`/?c=${encodeURIComponent(c.slug)}`}
              active={category === c.slug}
              onClick={onClose}
              count={c.noteCount}
              // No note uses it (in any state): it can go without touching a note.
              onDelete={
                c.totalCount === 0
                  ? () =>
                      deleteCategory.mutate(c.slug, {
                        onSuccess: () => category === c.slug && navigate("/"),
                      })
                  : undefined
              }
              deleteLabel={`Delete #${c.name} (no notes)`}
            >
              <span className="hash">#</span>
              {c.name}
            </NavItem>
          ))}
          {categories.data?.length === 0 && (
            <li className="nav-empty">
              Email <strong>{formatAddress(user.username, "idea")}</strong> to create one.
            </li>
          )}
        </ul>

        <ul className="nav nav-bottom">
          <NavItem to="/?view=archived" active={view === "archived"} onClick={onClose}>
            Archive
          </NavItem>
          <NavItem to="/?view=trashed" active={view === "trashed"} onClick={onClose}>
            Trash
          </NavItem>
          <NavItem to="/files" active={false} onClick={onClose}>
            Files
          </NavItem>
          <NavItem to="/x-notes" active={false} onClick={onClose}>
            Notes on X
          </NavItem>
          <NavItem to="/settings" active={false} onClick={onClose}>
            Settings
          </NavItem>
        </ul>
        {storage.data && <StorageMini usage={storage.data} onClick={onClose} />}
      </nav>
    </>
  );
}

/** Trash: notes here still count towards storage until deleted for good. */
function TrashBar() {
  const storage = useStorage();
  const empty = useEmptyTrash();
  const count = storage.data?.trashedNotes ?? 0;
  return (
    <div className="hint trash-bar">
      <span>Notes in Trash still use storage until you delete them for good.</span>
      {count > 0 && (
        <ConfirmButton
          label="Empty trash"
          confirmLabel={`Delete ${count} ${count === 1 ? "note" : "notes"} forever`}
          busy={empty.isPending}
          onConfirm={() => empty.mutate()}
        />
      )}
    </div>
  );
}

function NavItem({
  to,
  active,
  count,
  highlight,
  onClick,
  onDelete,
  deleteLabel,
  children,
}: {
  to: string;
  active: boolean;
  count?: number;
  highlight?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <li className={onDelete ? "nav-deletable" : undefined}>
      <Link to={to} className={`nav-item ${active ? "active" : ""}`} onClick={onClick}>
        <span className="nav-label">{children}</span>
        {count !== undefined && count > 0 && <span className={`count ${highlight ? "count-hot" : ""}`}>{count}</span>}
      </Link>
      {onDelete && (
        <button type="button" className="nav-delete" aria-label={deleteLabel} title={deleteLabel} onClick={onDelete}>
          ×
        </button>
      )}
    </li>
  );
}

function SearchBox({ initial }: { initial: string }) {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  useEffect(() => {
    if (value === initial) return;
    const t = setTimeout(() => setParams({ q: value.trim() || null }, { replace: true }), 300);
    return () => clearTimeout(t);
  }, [value, initial]);
  return (
    <div className="search">
      <input type="search" placeholder="Search notes" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Search notes" />
    </div>
  );
}

function NoteList({
  filter,
  openId,
  user,
}: {
  filter: { status: NoteStatus; category?: string; q?: string };
  openId: string | null;
  user: SessionUser;
}) {
  const notes = useNotes(filter);
  const items = useMemo(() => {
    const all = notes.data?.pages.flatMap((p) => p.notes) ?? [];
    return [...all.filter((n) => n.pinned), ...all.filter((n) => !n.pinned)];
  }, [notes.data]);

  if (notes.isPending) return <div className="list-state">Loading…</div>;
  if (notes.isError) return <div className="list-state">Couldn't load notes.</div>;
  if (items.length === 0) {
    return (
      <div className="list-state">
        {filter.q ? (
          <>No notes match “{filter.q}”.</>
        ) : filter.status === "active" ? (
          <>
            <p>No notes here yet.</p>
            <p className="muted small">
              Send or CC an email to <strong>{formatAddress(user.username, filter.category)}</strong>.
            </p>
          </>
        ) : (
          <>Nothing here.</>
        )}
      </div>
    );
  }

  return (
    <>
      <ul className="note-list">
        {items.map((note) => (
          <NoteRow key={note.id} note={note} active={note.id === openId} />
        ))}
      </ul>
      {notes.hasNextPage && (
        <button className="btn load-more" disabled={notes.isFetchingNextPage} onClick={() => notes.fetchNextPage()}>
          {notes.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </>
  );
}

function NoteRow({ note, active }: { note: Note; active: boolean }) {
  const preview = (note.format === "markdown" ? markdownToPlain(note.bodyText) : note.bodyText.replace(/\s+/g, " ")).slice(0, 140);
  return (
    <li>
      <button className={`note-row ${active ? "active" : ""}`} onClick={() => setParams({ note: note.id, new: null })}>
        <span className="note-row-top">
          <span className="note-title">
            {note.pinned && <span className="pin" aria-label="Pinned">●</span>}
            {note.title || "Untitled"}
          </span>
          <time className="muted small">{shortDate(note.createdAt)}</time>
        </span>
        {preview && <span className="note-preview">{preview}</span>}
        <span className="note-meta">
          {note.categories.map((c) => (
            <span key={c} className="tag">
              #{c}
            </span>
          ))}
          <span className="muted small">{note.fromAddr ?? sourceLabel(note.source)}</span>
        </span>
      </button>
    </li>
  );
}

function EmptyDetail({ user }: { user: SessionUser }) {
  return (
    <div className="empty-detail">
      <h2>How to save a note</h2>
      <ul className="howto">
        <li>
          <strong>CC</strong> <code>{user.address}</code> on any email you send.
        </li>
        <li>
          <strong>Forward</strong> an email to <code>{user.address}</code>.
        </li>
        <li>
          <strong>File it</strong> by adding a category: <code>{formatAddress(user.username, "shopping")}</code> or{" "}
          <code>{formatAddress(user.username, "shopping", "+")}</code>.
        </li>
      </ul>
      <p className="muted small">
        Mail from your own verified addresses is saved straight away. Anything else waits in Pending for you to approve.
      </p>
    </div>
  );
}

function shortDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

function MenuIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
